<template>
  <DashboardLayout>
    <template #header>
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Roles & Permissions
          </h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
            Manage user roles and permissions for your organization
          </p>
        </div>
        <div class="flex space-x-3">
          <Link :href="route('settings.permissions.matrix')">
            <Button variant="secondary" size="md">
              <TableCellsIcon class="w-5 h-5 mr-2" />
              Permission Matrix
            </Button>
          </Link>
          <Link v-if="canManageRoles" :href="route('settings.roles.create')">
            <Button variant="primary" size="md">
              <PlusIcon class="w-5 h-5 mr-2" />
              Create Role
            </Button>
          </Link>
        </div>
      </div>
    </template>

    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Statistics Overview -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Roles</p>
              <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ totalRoles }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {{ systemRoles.length }} system, {{ customRoles.length }} custom
              </p>
            </div>
            <div class="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ShieldCheckIcon class="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Permissions</p>
              <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ totalPermissions }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Across all modules
              </p>
            </div>
            <div class="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <KeyIcon class="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Custom Roles</p>
              <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ customRoles.length }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Created by your team
              </p>
            </div>
            <div class="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <UserGroupIcon class="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
      </div>

      <!-- System Roles Section -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
                System Roles
              </h2>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Default roles that cannot be modified or deleted
              </p>
            </div>
            <Badge variant="info" size="sm">
              {{ systemRoles.length }} roles
            </Badge>
          </div>
        </template>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="role in systemRoles"
            :key="role.id"
            class="relative border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 hover:shadow-md transition-all"
          >
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <div class="flex items-center space-x-2 mb-1">
                  <h3 class="font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {{ role.name }}
                  </h3>
                  <Badge variant="secondary" size="xs">System</Badge>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                  {{ role.description || 'No description available' }}
                </p>
              </div>
            </div>

            <div class="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <div class="flex items-center space-x-1">
                <KeyIcon class="w-4 h-4" />
                <span>{{ role.permissions_count }} permissions</span>
              </div>
              <div class="flex items-center space-x-1">
                <UsersIcon class="w-4 h-4" />
                <span>{{ role.users_count }} users</span>
              </div>
            </div>

            <div class="mt-4">
              <Link :href="route('settings.roles.show', role.id)">
                <Button size="sm" variant="ghost" class="w-full justify-center">
                  <EyeIcon class="w-4 h-4 mr-2" />
                  View Details
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>

      <!-- Custom Roles Section -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
                Custom Roles
              </h2>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Roles created by your organization
              </p>
            </div>
            <div class="flex items-center space-x-2">
              <Badge variant="primary" size="sm">
                {{ customRoles.length }} roles
              </Badge>
              <Link v-if="canManageRoles" :href="route('settings.roles.create')">
                <Button size="sm" variant="primary">
                  <PlusIcon class="w-4 h-4 mr-2" />
                  New Role
                </Button>
              </Link>
            </div>
          </div>
        </template>

        <!-- Empty State -->
        <div v-if="customRoles.length === 0" class="text-center py-16">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
            <ShieldExclamationIcon class="h-8 w-8 text-gray-400 dark:text-gray-600" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No custom roles yet
          </h3>
          <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Create custom roles tailored to your organization's specific needs and workflows.
          </p>
          <Link v-if="canManageRoles" :href="route('settings.roles.create')">
            <Button variant="primary">
              <PlusIcon class="w-5 h-5 mr-2" />
              Create Your First Role
            </Button>
          </Link>
        </div>

        <!-- Roles Grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="role in customRoles"
            :key="role.id"
            class="relative border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-white dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all"
          >
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <h3 class="font-semibold text-lg text-gray-900 dark:text-gray-100 mb-1">
                  {{ role.name }}
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {{ role.description || 'No description provided' }}
                </p>
              </div>
            </div>

            <div class="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <div class="flex items-center space-x-1">
                <KeyIcon class="w-4 h-4" />
                <span>{{ role.permissions_count }}</span>
              </div>
              <div class="flex items-center space-x-1">
                <UsersIcon class="w-4 h-4" />
                <span>{{ role.users_count }}</span>
              </div>
            </div>

            <div class="flex items-center space-x-2 mt-4">
              <Link :href="route('settings.roles.show', role.id)" class="flex-1">
                <Button size="sm" variant="ghost" class="w-full justify-center">
                  <EyeIcon class="w-4 h-4 mr-2" />
                  View
                </Button>
              </Link>
              <Link v-if="canManageRoles" :href="route('settings.roles.edit', role.id)" class="flex-1">
                <Button size="sm" variant="primary" class="w-full justify-center">
                  <PencilIcon class="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </Link>
            </div>

            <!-- Action Buttons -->
            <div v-if="canManageRoles" class="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                @click="duplicateRole(role.id)"
                class="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title="Duplicate role"
              >
                <DocumentDuplicateIcon class="h-4 w-4" />
                <span>Duplicate</span>
              </button>
              <button
                v-if="role.users_count === 0"
                @click="confirmDeleteRole(role)"
                class="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Delete role"
              >
                <TrashIcon class="h-4 w-4" />
                <span>Delete</span>
              </button>
              <span
                v-else
                class="flex items-center space-x-1 text-xs text-gray-400 dark:text-gray-600 cursor-not-allowed"
                title="Cannot delete role with assigned users"
              >
                <TrashIcon class="h-4 w-4" />
                <span>Delete</span>
              </span>
            </div>
          </div>
        </div>
      </Card>

      <!-- Quick Actions Card -->
      <Card v-if="canManageRoles">
        <template #header>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Quick Actions
          </h2>
        </template>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link :href="route('settings.permissions.matrix')">
            <div class="flex items-center p-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-900/30 dark:hover:to-blue-800/30 transition-all cursor-pointer border border-blue-200 dark:border-blue-800">
              <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                <TableCellsIcon class="h-6 w-6 text-white" />
              </div>
              <div class="ml-4">
                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Permission Matrix
                </h3>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  View and manage all permissions in a grid
                </p>
              </div>
              <ChevronRightIcon class="ml-auto h-5 w-5 text-gray-400 dark:text-gray-600" />
            </div>
          </Link>

          <Link :href="route('settings.roles.create')">
            <div class="flex items-center p-4 rounded-lg bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 hover:from-purple-100 hover:to-purple-200 dark:hover:from-purple-900/30 dark:hover:to-purple-800/30 transition-all cursor-pointer border border-purple-200 dark:border-purple-800">
              <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-500 dark:bg-purple-600 flex items-center justify-center">
                <PlusCircleIcon class="h-6 w-6 text-white" />
              </div>
              <div class="ml-4">
                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Create from Template
                </h3>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Start with a pre-configured role template
                </p>
              </div>
              <ChevronRightIcon class="ml-auto h-5 w-5 text-gray-400 dark:text-gray-600" />
            </div>
          </Link>
        </div>
      </Card>
    </div>

    <!-- Delete Confirmation Modal -->
    <Modal :show="showDeleteModal" @close="showDeleteModal = false">
      <template #title>
        Delete Role
      </template>

      <template #content>
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <ExclamationTriangleIcon class="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Are you sure you want to delete this role?
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            You are about to delete the role <strong>"{{ roleToDelete?.name }}"</strong>.
            This action cannot be undone.
          </p>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <Button variant="secondary" @click="showDeleteModal = false">
            Cancel
          </Button>
          <Button variant="danger" @click="deleteRole">
            <TrashIcon class="w-4 h-4 mr-2" />
            Delete Role
          </Button>
        </div>
      </template>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  PlusIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  KeyIcon,
  UsersIcon,
  UserGroupIcon,
  EyeIcon,
  PencilIcon,
  ShieldExclamationIcon,
  PlusCircleIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/vue/24/outline';
import { useAuth } from '@/Composables/useAuth';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  systemRoles: {
    type: Array,
    required: true,
  },
  customRoles: {
    type: Array,
    required: true,
  },
  totalRoles: {
    type: Number,
    default: 0,
  },
});

const { can } = useAuth();
const { success, error: showError } = useToast();

// Permission checks
const canManageRoles = computed(() => can('system.roles.manage'));

// Stats computed properties
const totalRoles = computed(() => props.systemRoles.length + props.customRoles.length);
const totalPermissions = computed(() => {
  const allRoles = [...props.systemRoles, ...props.customRoles];
  const uniquePermissions = new Set();

  allRoles.forEach(role => {
    if (role.permissions_count) {
      // This is a simplified calculation - actual unique count would need backend data
      uniquePermissions.add(role.permissions_count);
    }
  });

  // Return a reasonable estimate or use a prop if provided
  return props.totalPermissions || 200; // Default estimate
});

// Delete modal state
const showDeleteModal = ref(false);
const roleToDelete = ref(null);

/**
 * Duplicate a role
 */
const duplicateRole = (roleId) => {
  router.post(route('settings.roles.duplicate', roleId), {}, {
    onSuccess: () => {
      success('Role duplicated successfully. You can now edit it.');
    },
    onError: (errors) => {
      showError(errors.message || 'Failed to duplicate role. Please try again.');
    },
  });
};

/**
 * Show delete confirmation modal
 */
const confirmDeleteRole = (role) => {
  roleToDelete.value = role;
  showDeleteModal.value = true;
};

/**
 * Delete a role
 */
const deleteRole = () => {
  if (!roleToDelete.value) return;

  router.delete(route('settings.roles.destroy', roleToDelete.value.id), {
    onSuccess: () => {
      success('Role deleted successfully.');
      showDeleteModal.value = false;
      roleToDelete.value = null;
    },
    onError: (errors) => {
      showError(errors.message || 'Failed to delete role. Please try again.');
      showDeleteModal.value = false;
      roleToDelete.value = null;
    },
  });
};
</script>
